import React, { Suspense, lazy, useEffect, useRef } from "react"
import sanitizeHtml from "sanitize-html-react"

// Quill is a CDN script: loaded on first edit, so the builder opens without it.
const QuillInput = lazy(() => import("@react/shared/quillInput"))
const hasQuill   = () => typeof window !== "undefined" && !!window.Quill

// Older and imported lines are plain text.
export const isHtml     = text => /<\/?[a-z][^>]*>/i.test(text || "")
const escapeHtml        = text => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
export const toHtml     = text => {
  const value = text || ""
  return isHtml(value) ? value : value.split(/\r?\n/).map(line => `<p>${escapeHtml(line) || "<br>"}</p>`).join("")
}
const decodeEntities = text => text
  .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
  .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, "&")

// Tags go through sanitize-html, never a regular expression or an HTML parser (CodeQL).
export const toPlainText = html => decodeEntities(
  sanitizeHtml(String(html || "").replace(/<\/(p|li)>/gi, "$&\n"), { allowedTags: [], allowedAttributes: {} })
).replace(/\u00a0/g, " ").replace(/\n$/, "")
export const isBlank    = html => !toPlainText(html).trim()

const SANITIZE = {
  allowedTags:       ["p", "br", "strong", "b", "em", "i", "u", "ol", "ul", "li", "span"],
  allowedAttributes: {},
}

export const RichTextView = ({ value, className = "", ...props }) => (
  <div
    {...props}
    className               = {`qb-rich break-words ${className}`}
    dangerouslySetInnerHTML = {{ __html: sanitizeHtml(toHtml(value), SANITIZE) }}
  />
)

export const RichTextEditor = ({ value, placeholder, onChange, onDone, onCancel, onEnter, className = "" }) => {
  const wrapperRef = useRef(null)
  const draftRef   = useRef(toHtml(value))
  const closedRef  = useRef(false)

  const update = html => {
    draftRef.current = html
    onChange?.(isBlank(html) ? "" : html)
  }
  const finish = handler => {
    if (closedRef.current) return
    closedRef.current = true
    handler?.(isBlank(draftRef.current) ? "" : draftRef.current)
  }

  useEffect(() => {
    if (!hasQuill()) return undefined
    let tries = 0
    const timer = setInterval(() => {
      const container = wrapperRef.current?.querySelector(".ql-container")
      const quill     = container && window.Quill.find(container)
      if (quill) quill.setSelection(quill.getLength(), 0)
      if (quill || ++tries > 200) clearInterval(timer)
    }, 50)
    return () => clearInterval(timer)
  }, [])

  // A toolbar click blurs the editor for a moment.
  const onBlur = () => {
    setTimeout(() => {
      if (wrapperRef.current && !wrapperRef.current.contains(document.activeElement)) finish(onDone)
    }, 0)
  }

  return (
    <div
      ref         = {wrapperRef}
      onClick     = {event => event.stopPropagation()}
      onBlur      = {onBlur}
      onMouseDown = {event => { if (event.target.closest(".ql-toolbar")) event.preventDefault() }}
      onKeyDown   = {event => {
        if (event.key === "Escape") { event.preventDefault(); finish(onCancel) }
      }}
      className   = {`qb-rte qb-rich ${className}`}
    >
      {hasQuill() ? (
        <Suspense fallback={null}>
          <QuillInput
            text        = {draftRef.current}
            config      = "compact"
            placeholder = {placeholder}
            onChange    = {update}
            onEnter     = {onEnter && (() => finish(onEnter))}
          />
        </Suspense>
      ) : (
        <textarea
          autoFocus
          rows        = {2}
          placeholder = {placeholder}
          defaultValue = {toPlainText(draftRef.current)}
          onChange    = {event => update(toHtml(event.target.value))}
          onKeyDown   = {event => {
            if (onEnter && event.key === "Enter" && !event.shiftKey) { event.preventDefault(); finish(onEnter) }
          }}
          className   = "block w-full border-0 bg-transparent outline-none px-3 py-2 resize-y"
        />
      )}
    </div>
  )
}
